import { Lead, GuideSection, H3, P, UL, OL, LI, GuideLink } from './guide-ui';

/**
 * Nederlandstalige gids (locale: 'nl', logpulse.nl): het meldplicht-draaiboek
 * onder de Cyberbeveiligingswet. Bewust in draaiboek-vorm en GEO-vriendelijk
 * geschreven: elke sectie opent met het directe antwoord, de deadlines en
 * verplichte meldvelden staan als opsomming, en de FAQ spiegelt het
 * FAQPage-JSON-LD dat GuidePage automatisch uitstoot. Feiten consistent met
 * de Cbw-gids (Nis2CyberbeveiligingswetGuide).
 */
export function MeldplichtCbwGuideBody() {
  return (
    <>
      <Lead>
        Sinds 15 augustus 2026 geldt onder de Cyberbeveiligingswet een
        meldplicht voor significante incidenten: een vroegtijdige waarschuwing
        binnen 24 uur, een volledige melding binnen 72 uur en een eindrapport
        binnen één maand. Dit draaiboek beschrijft wat je wanneer moet melden,
        bij wie, en vooral: wat je vandaag klaarzet zodat die eerste 24 uur
        geen paniek worden.
      </Lead>

      <GuideSection id="wat-is-de-meldplicht" title="Wat houdt de meldplicht in?">
        <P>
          De meldplicht verplicht essentiële en belangrijke entiteiten om een
          significant incident te melden bij het CSIRT (voor de meeste
          organisaties het NCSC) en bij de toezichthouder. De melding verloopt
          in drie stappen met harde deadlines, die beginnen te tellen zodra je
          van het incident op de hoogte bent. De meldplicht geldt voor beide
          categorieën entiteiten even zwaar; het verschil zit in het toezicht,
          niet in de deadlines.
        </P>
        <P>
          De plicht staat niet op zichzelf. Zij vormt samen met de zorgplicht
          (beveiligingsmaatregelen, waaronder logging en detectie) en de
          registratieplicht bij het NCSC de kern van de{' '}
          <GuideLink href="/guides/nis2-cyberbeveiligingswet">
            Cyberbeveiligingswet
          </GuideLink>
          , de Nederlandse implementatie van NIS2. Het bestuur is persoonlijk
          verantwoordelijk voor de naleving.
        </P>
        <P>
          Weet je niet zeker of jouw organisatie onder de wet valt? Doe de
          gratis check op{' '}
          <GuideLink href="https://cyberplicht.nl/">cyberplicht.nl</GuideLink>:
          drie vragen, zestig seconden, anoniem. Je ziet direct of je een
          essentiële of belangrijke entiteit bent, of dat je via
          ketenverplichtingen wordt geraakt.
        </P>
      </GuideSection>

      <GuideSection id="wat-is-significant" title="Wat telt als een significant incident?">
        <P>
          Een incident is significant als het een ernstige operationele
          verstoring van je diensten of financiële verliezen veroorzaakt of
          kan veroorzaken, of als het andere personen of organisaties
          aanzienlijke materiële of immateriële schade toebrengt of kan
          toebrengen. Let op het woord &ldquo;kan&rdquo;: ook een incident
          waarvan de schade nog niet vaststaat maar wel reëel dreigt, valt
          onder de meldplicht.
        </P>
        <P>
          In de praktijk: een ransomware-besmetting, een aanvaller met toegang
          tot je systemen, een storing die een essentiële dienst raakt of een
          datalek met impact op derden zijn vrijwel altijd significant. Twijfel
          je, meld dan. Een melding is geen schuldbekentenis en het NCSC helpt
          juist bij de duiding; te laat melden is wél een overtreding.
        </P>
      </GuideSection>

      <GuideSection id="de-meldketen" title="De meldketen: 24 uur, 72 uur, één maand">
        <OL>
          <LI>
            <strong>Binnen 24 uur: de vroegtijdige waarschuwing.</strong> Een
            korte eerste melding met wat je op dat moment weet: of je vermoedt
            dat het incident door kwaadwillig handelen is veroorzaakt en of
            het grensoverschrijdende gevolgen kan hebben. Deze melding mag
            beperkt zijn; wachten tot je het volledige beeld hebt is de fout
            die je niet mag maken.
          </LI>
          <LI>
            <strong>Binnen 72 uur: de volledige melding.</strong> Een
            incidentmelding met je eerste beoordeling van de ernst en de
            impact, en de indicatoren van compromittatie (IOC&rsquo;s) die je
            op dat moment hebt. Hier moet je dus al kunnen laten zien wat er in
            je systemen gebeurd is.
          </LI>
          <LI>
            <strong>Binnen één maand: het eindrapport.</strong> Een
            gedetailleerde beschrijving van het incident, de vermoedelijke
            oorzaak, de genomen en lopende maatregelen en de eventuele
            grensoverschrijdende gevolgen. Loopt het incident dan nog, dan
            volgt het eindrapport een maand na afronding; het CSIRT kan
            tussentijds om een statusverslag vragen.
          </LI>
        </OL>
        <P>
          Alle drie de stappen meld je bij het CSIRT en de toezichthouder; voor
          de meeste organisaties is dat één loket via het meldportaal van het
          NCSC. Wie je toezichthouder is hangt af van je sector: voor veel
          sectoren is dat de Rijksinspectie Digitale Infrastructuur (RDI),
          voor bijvoorbeeld de financiële sector DNB.
        </P>
      </GuideSection>

      <GuideSection id="draaiboek" title="Het draaiboek: zes dingen die je nu klaarzet">
        <OL>
          <LI>
            <strong>Beleg het meldproces.</strong> Wijs aan wie de melding
            doet, wie diens vervanger is en wie het bestuur informeert. De
            24-uursklok loopt ook in het weekend en tijdens vakanties.
          </LI>
          <LI>
            <strong>Regel de registratie en toegang tot het meldportaal.</strong>{' '}
            De registratieplicht bij het NCSC geldt sowieso; zorg dat de
            accounts en contactgegevens kloppen vóórdat je ze om 03:00
            &rsquo;s nachts nodig hebt.
          </LI>
          <LI>
            <strong>Leg je classificatiecriteria vast.</strong> Beschrijf
            wanneer een incident voor jouw organisatie significant is, met
            concrete drempels per dienst. Dat voorkomt discussie op het
            moment zelf en toont de toezichthouder dat je het proces beheerst.
          </LI>
          <LI>
            <strong>Centraliseer je logging en zet detectie aan.</strong>{' '}
            Binnen 24 uur iets zinnigs zeggen over kwaadwillig handelen kan
            alleen als de relevante logs al bestaan, doorzoekbaar zijn en
            detectie het incident heeft opgemerkt. Dit is de technische
            fundering onder de hele meldketen.
          </LI>
          <LI>
            <strong>Maak een meldtemplate.</strong> Eén document met de velden
            van alle drie de meldingen (zie hieronder), vooraf ingevuld met de
            vaste gegevens van je organisatie. In een crisis wil je invullen,
            niet bedenken.
          </LI>
          <LI>
            <strong>Oefen de keten.</strong> Eén tabletop-oefening per jaar
            waarin je een fictief incident binnen 24 uur tot een ingevulde
            vroegtijdige waarschuwing brengt, leert je meer dan tien
            beleidsdocumenten.
          </LI>
        </OL>
      </GuideSection>

      <GuideSection id="welke-informatie" title="Welke informatie moet in elke melding?">
        <H3>Vroegtijdige waarschuwing (24 uur)</H3>
        <UL>
          <LI>dat er een significant incident is, en sinds wanneer;</LI>
          <LI>of je kwaadwillig handelen vermoedt;</LI>
          <LI>of het incident grensoverschrijdende gevolgen kan hebben.</LI>
        </UL>
        <H3>Volledige melding (72 uur)</H3>
        <UL>
          <LI>een eerste beoordeling van ernst en impact;</LI>
          <LI>de getroffen diensten en systemen;</LI>
          <LI>beschikbare indicatoren van compromittatie (IOC&rsquo;s).</LI>
        </UL>
        <H3>Eindrapport (één maand)</H3>
        <UL>
          <LI>een gedetailleerde beschrijving van het incident en het verloop;</LI>
          <LI>de vermoedelijke oorzaak;</LI>
          <LI>de genomen en lopende maatregelen;</LI>
          <LI>de eventuele grensoverschrijdende gevolgen.</LI>
        </UL>
        <P>
          De rode draad: elke fase vraagt méér onderbouwing uit je logs. De
          vroegtijdige waarschuwing draait op detectie, de 72-uursmelding op
          doorzoekbare logs en IOC&rsquo;s, het eindrapport op een volledige
          reconstructie inclusief oorzaak.
        </P>
      </GuideSection>

      <GuideSection id="sancties" title="Wat als je te laat of niet meldt?">
        <P>
          Niet of te laat melden is een zelfstandige overtreding, los van de
          vraag of je beveiliging op orde was. De boetes lopen op tot{' '}
          <strong>10 miljoen euro of 2%</strong> van de wereldwijde jaaromzet
          voor essentiële entiteiten en{' '}
          <strong>7 miljoen euro of 1,4%</strong> voor belangrijke entiteiten.
          De toezichthouder kan daarnaast bindende aanwijzingen geven en bij
          essentiële entiteiten als uiterste middel bestuurders tijdelijk laten
          schorsen. Het bestuur is persoonlijk verantwoordelijk voor het
          meldproces.
        </P>
      </GuideSection>

      <GuideSection id="logs-als-bewijs" title="Zo onderbouw je een melding met logs">
        <P>
          Elke deadline in de meldketen leunt op dezelfde fundering: logs die
          al bestaan, op één plek doorzoekbaar zijn en bewaakt worden door
          detectie. Dat is precies de kant die LogPulse dekt: gecentraliseerd
          logbeheer met instelbare retentie, realtime detecties met MITRE
          ATT&amp;CK-mapping in de{' '}
          <GuideLink href="/siem">LogPulse SIEM</GuideLink>, en
          incident-workflows die de 24-uurs- en 72-uursmeldingen onderbouwen
          met tijdlijnen, getroffen systemen en IOC&rsquo;s. De data blijft in
          de EU (GCP Amsterdam).
        </P>
        <P>
          Voor de duidelijkheid: geen enkel product maakt je vanzelf compliant,
          en de meldplicht is uiteindelijk een proces van mensen. Maar het
          verschil tussen een melding die binnen 24 uur staat en een die te
          laat komt, is vrijwel altijd of de logging en detectie er al waren.
          Zie{' '}
          <GuideLink href="/compliance/nis2">
            NIS2-compliance met LogPulse
          </GuideLink>{' '}
          voor hoe dat eruitziet, of begin gratis en zet je eerste detecties
          vandaag aan.
        </P>
      </GuideSection>
    </>
  );
}
